import React, { type KeyboardEvent, type MouseEvent, useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX } from '../../../../../../common/constants';
import type { Field } from '../../../../../../common/constants/fields';
import type { SupportedThirdParty } from '../../../../../../common/constants/import-type';
import type { ThirdPartyFieldMapping } from '../../../../../../common/types';
import {
	DropdownSectionDivider,
	DropdownSectionHeader,
	DropdownSectionWrapper,
} from '../../../../../../common/ui';
import { FieldSelectorItem } from '../../../../../../common/ui/field-selector-item';
import { useContentForThirdParty } from '../../../../../../common/utils';
import { getIcon } from '../../../../../../common/utils/fields';
import { getHierarchyMappingItems } from '../../../../../../common/utils/third-party-mapping';
import { useThirdPartyFieldConfigState } from '../../../../../../controllers/import-state';
import { useThirdPartyHierarchyState } from '../../../../../../controllers/import-state/selectors';
import { useUIState } from '../../../../../../controllers/ui-state';
import messages from './messages';

type Props = {
	thirdParty: SupportedThirdParty;
	thirdPartyFieldMapping: ThirdPartyFieldMapping;
	onClickSelectCustomField: (event: MouseEvent<Element> | KeyboardEvent<Element>) => void;
};

export const ExistingFieldsSection = ({
	thirdParty,
	thirdPartyFieldMapping: {
		compatibleExistingFieldTypes,
		mappedJiraField,
		thirdPartyField,
		compatibleNewFieldTypes,
	},
	onClickSelectCustomField,
}: Props) => {
	const { formatMessage } = useIntl();
	const [thirdPartyFieldConfigState, { setThirdPartyFieldMapping }] =
		useThirdPartyFieldConfigState();
	const { disabledFieldTooltip } = useContentForThirdParty(thirdParty);
	const [thirdPartyHierarchyState] = useThirdPartyHierarchyState({
		types: getHierarchyMappingItems(thirdParty),
		formatMessage,
	});
	const [, { setUIState }] = useUIState();

	const getSelectedCustomField = useCallback(() => {
		if (mappedJiraField.fieldId) return null;

		return (
			<FieldSelectorItem
				icon={getIcon({ fieldType: mappedJiraField.fieldType, isSelected: true })}
				name={mappedJiraField.fieldName}
				isSelected
			/>
		);
	}, [mappedJiraField]);

	const handleDropdownItemClickFactory = useCallback(
		(jiraField: Field) => () => {
			setThirdPartyFieldMapping(thirdPartyField.fieldId, jiraField);
			setUIState({ hasUnsavedFieldMappingChanges: true });
		},
		[setThirdPartyFieldMapping, setUIState, thirdPartyField.fieldId],
	);

	const availableJiraFieldItems = useMemo(() => {
		if (thirdPartyFieldConfigState === null) return null;

		const { availableJiraFields, includedJiraFieldIds } = thirdPartyFieldConfigState;

		return compatibleExistingFieldTypes
			.flatMap((fieldType) => availableJiraFields[fieldType] ?? [])
			.sort((jiraField) =>
				!!mappedJiraField.fieldId && jiraField.fieldId === mappedJiraField.fieldId ? -1 : 0,
			)
			.map((jiraField) => {
				const isSelected =
					!!mappedJiraField?.fieldId && jiraField.fieldId === mappedJiraField.fieldId;
				const isDisabled = !!jiraField.fieldId && !!includedJiraFieldIds.has(jiraField.fieldId);
				const shouldExcludeField =
					ff('jwm-x2c-hierarchy-mapping_kkjnd') &&
					thirdPartyHierarchyState?.selectedHierarchyMappedItem?.fieldId === jiraField.fieldId;

				return (
					<FieldSelectorItem
						key={`${jiraField.fieldName}-${jiraField.fieldType}`}
						name={jiraField.fieldName}
						icon={getIcon({
							fieldType: jiraField.fieldType,
							isSelected,
							isDisabled,
						})}
						onClick={handleDropdownItemClickFactory(jiraField)}
						isDisabled={isDisabled || shouldExcludeField}
						isSelected={isSelected}
						tooltipContent={disabledFieldTooltip}
					/>
				);
			});
	}, [
		compatibleExistingFieldTypes,
		disabledFieldTooltip,
		handleDropdownItemClickFactory,
		mappedJiraField.fieldId,
		thirdPartyFieldConfigState,
		thirdPartyHierarchyState?.selectedHierarchyMappedItem?.fieldId,
	]);

	const showNewFieldTypes = compatibleNewFieldTypes.length > 0;
	return (
		<Wrapper role="group">
			{!!availableJiraFieldItems && availableJiraFieldItems.length > 0 && (
				<>
					<DropdownSectionWrapper
						id={`${THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX}.existing-fields.header`}
					>
						<Box xcss={headerStyle}>
							<Text size="UNSAFE_small">{formatMessage(messages.existingFieldSectionHeading)}</Text>
						</Box>
					</DropdownSectionWrapper>
					<DropdownItemSection>
						<DropdownItemGroup
							testId={`${THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX}.existing-fields-section.dropdown-item-group`}
							aria-labelledby={`${THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX}.existing-fields.header`}
						>
							{availableJiraFieldItems}
						</DropdownItemGroup>
					</DropdownItemSection>
					{showNewFieldTypes && <DropdownSectionDivider />}
				</>
			)}
			{showNewFieldTypes && (
				<FooterWrapper>
					<DropdownItem
						testId={`${THIRD_PARTY_FIELD_SELECTOR_TEST_ID_PREFIX}.existing-fields-section.new-custom-field-button`}
						onClick={onClickSelectCustomField}
						elemAfter={<ChevronRightIcon label="" />}
					>
						<DropdownSectionHeader>
							{formatMessage(messages.setNewFieldTypeHeading)}
						</DropdownSectionHeader>
					</DropdownItem>
					{getSelectedCustomField()}
				</FooterWrapper>
			)}
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	maxHeight: '50vh',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownItemSection = styled.div({
	display: 'flex',
	flexDirection: 'column',
	overflowY: 'auto',
	flexGrow: '1',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterWrapper = styled.div({
	flex: '0 1 auto',
});

const headerStyle = xcss({
	textTransform: 'uppercase',
});
